import React from "react";
import { TowerControl, Bell, TriangleAlert, Info, CheckCircle2, Loader2, BellRing } from "lucide-react";

const SEV = {
  emergency: { label: "Emergency", Icon: TowerControl, c: "#FF7089", bg: "rgba(255,92,122,0.16)" },
  warning: { label: "Warning", Icon: Bell, c: "#FFC726", bg: "rgba(255,199,38,0.16)" },
  advisory: { label: "Advisory", Icon: TriangleAlert, c: "#5B9BFF", bg: "rgba(91,155,255,0.16)" },
  info: { label: "Notice", Icon: Info, c: "#93A4C0", bg: "rgba(255,255,255,0.07)" },
};

const AlertsScreen = ({ items, loading, notifState, onEnableNotifications }) => {
  const emergency = items.find((a) => a.severity === "emergency");
  const rest = items.filter((a) => a !== emergency);

  return (
    <div className="go-scroll min-h-full overflow-y-auto px-5 pb-28 pt-[max(env(safe-area-inset-top),16px)]">
      <header className="go-fade-up mb-5">
        <h1 className="font-head text-2xl font-extrabold text-white">Public alerts</h1>
        <p className="mt-1 text-sm text-[#8194B2]">Official advisories from the National Command Centre.</p>
      </header>

      {notifState !== "granted" && (
        <button onClick={onEnableNotifications} data-testid="enable-notifications"
          className="go-fade-up mb-5 flex w-full items-center gap-3 rounded-2xl border border-[#FFC726]/30 bg-[#FFC726]/10 px-4 py-3.5 text-left transition-transform active:scale-[0.98]">
          <BellRing size={22} color="#FFC726" strokeWidth={2.3} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-white">Turn on instant alerts</div>
            <div className="text-xs text-[#B9C6DE]">Get notified the moment a storm warning or national alert is issued.</div>
          </div>
          <span className="rounded-full bg-[#FFC726] px-3 py-1.5 text-xs font-bold text-[#0A2A6B]">Enable</span>
        </button>
      )}

      {loading && <div className="flex items-center justify-center py-16 text-[#67799A]"><Loader2 className="animate-spin" /></div>}

      {!loading && emergency && (
        <div className="go-fade-up mb-5 rounded-[1.75rem] bg-[#EF4444] p-6 text-white" style={{ boxShadow: "0 12px 40px rgba(239,68,68,0.4)" }} data-testid="alerts-emergency-banner">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide">
            <TowerControl size={16} /> Emergency Alert
          </div>
          <h2 className="font-head text-xl font-extrabold">{emergency.title}</h2>
          <p className="mt-1.5 text-sm text-white/90">{emergency.body}</p>
          <div className="mt-3 text-xs text-white/70">{emergency.target} · {emergency.category}</div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="go-fade-up flex flex-col items-center justify-center py-16 text-center" data-testid="alerts-all-clear">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#22E0A1]/15">
            <CheckCircle2 size={44} color="#22E0A1" strokeWidth={2.3} />
          </div>
          <h2 className="font-head mt-5 text-xl font-bold text-white">All clear</h2>
          <p className="mt-2 text-sm text-[#8194B2]">No active advisories right now. We'll alert you instantly if that changes.</p>
        </div>
      )}

      <div className="space-y-4">
        {rest.map((a) => {
          const m = SEV[a.severity] || SEV.info;
          const Icon = m.Icon;
          return (
            <article key={a.id} className="go-card rounded-3xl p-5" data-testid={`alert-card-${a.id}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: m.bg, color: m.c }}>
                  <Icon size={13} strokeWidth={2.5} /> {m.label}
                </span>
                {typeof a.minutes_ago === "number" && <span className="text-xs text-[#67799A]">{a.minutes_ago}m ago</span>}
              </div>
              <h3 className="font-head text-base font-bold text-white">{a.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-[#A9B8D2]">{a.body}</p>
              <div className="mt-2.5 text-xs text-[#67799A]">{a.target} · {a.category}</div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default AlertsScreen;
